/**
 * NFA module design tokens.
 * Light values are the specified palette, byte-for-byte. The dark variant is
 * derived from the same hues so the module stays legible for a user who has
 * the platform's global dark toggle on.
 */
import { useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export const useNfaTheme = () => {
  const { isDark } = useTheme();
  return useMemo(() => (isDark ? {
    isDark: true,
    primary: '#4F86D6', primaryDeep: '#1E4E8C', primaryLight: '#7FA8E4',
    accent: '#F26B1F',
    ink: '#F4F6FA', body: '#C3CCDA', muted: '#8695AC',
    surface: '#161C27', surfaceAlt: '#1D2533', page: '#0E1218',
    border: '#2B3440', borderStrong: '#3A4452',
    green: '#4CAF54', amber: '#E0912A', red: '#E05A52',
    tint: (hex, a = '1A') => `${hex}${a}`,
  } : {
    isDark: false,
    primary: '#1E4E8C', primaryDeep: '#143A6B', primaryLight: '#4F86D6',
    accent: '#F26B1F',
    ink: '#0F1729', body: '#4A5568', muted: '#8695AC',
    surface: '#FFFFFF', surfaceAlt: '#F9FBFE', page: '#F4F6FA',
    border: '#D9E0EB', borderStrong: '#C3CCDA',
    green: '#2E7D32', amber: '#B26A00', red: '#B3261E',
    tint: (hex, a = '14') => `${hex}${a}`,
  }), [isDark]);
};

export const STATUS_TONE = {
  DRAFT: 'muted', SUBMITTED: 'primary', UNDER_REVIEW: 'amber',
  APPROVED: 'green', REJECTED: 'red', RETURNED: 'accent', WITHDRAWN: 'muted',
  PENDING: 'amber', DELEGATED: 'primary', SKIPPED: 'muted',
  connected: 'green', degraded: 'amber', down: 'red',
  SYNCED: 'green', FAILED: 'red', NOT_SYNCED: 'muted', CONFLICT: 'accent',
  REPLAYED: 'green',
};

export const money = (v, cur = 'INR') => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  const sym = { INR: '₹', USD: '$', EUR: '€', GBP: '£' }[cur] || `${cur} `;
  if (cur === 'INR') {
    if (a >= 1e7) return `${sym}${(n / 1e7).toFixed(2)} Cr`;
    if (a >= 1e5) return `${sym}${(n / 1e5).toFixed(2)} L`;
  } else if (a >= 1e6) return `${sym}${(n / 1e6).toFixed(2)} M`;
  return `${sym}${n.toLocaleString('en-IN')}`;
};

export const day = (v) => (v ? new Date(v).toLocaleDateString('en-GB',
  { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const stamp = (v) => (v ? new Date(v).toLocaleString('en-GB',
  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default useNfaTheme;
